import { AxiosRequestConfig } from "axios";
import { callExternalApi, ApiResponse } from "./external-api.service";
import { BackendResponse } from "../models/api-response";
import { getLogger } from "src/utils/logger";

const logger = getLogger('strategies.service');
const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

export interface Strategy {
  id: number;
  text: string;
}

export interface StrategiesResponse {
  strategies: Strategy[];
  documentId: string;
}

/**
 * Generate strategies based on the provided smart goal
 *
 * @param smartGoal The selected smart goal
 * @param numStrategies Number of strategies to generate
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to an ApiResponse containing the strategies or an error
 */
export const generateStrategies = async (
  data: { smartGoal: string, numStrategies: number },
  accessToken: string
): Promise<ApiResponse<StrategiesResponse>> => {
  const { smartGoal, numStrategies } = data;
  // Create a clean data object without any potential circular references
  const requestData = {
    smartGoal: String(smartGoal),
    numStrategies: Number(numStrategies)
  };
  
  const config: AxiosRequestConfig = {
    url: `${apiServerUrl}/api/documents/strategies`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: requestData,
  };
  
  logger.debug("Generating strategies");
  const response = await callExternalApi<BackendResponse>({ config });
  logger.debug(`Response received with status code: ${response.data?.status || 'unknown'}`);
  
  if (response.error || !response.data) {
    return {
      data: null,
      error: response.error || { message: "Failed to generate strategies" }
    };
  }
  
  try {
    // Extract content from the backend response
    const { content, document_id } = response.data;
    
    // Parse content if it's a string, otherwise use as is
    const strategiesContent = typeof content === 'string' 
      ? JSON.parse(content) 
      : content;
    
    // Log safely without risking circular references
    logger.debug(`Strategies content received with ${strategiesContent.strategies?.length || 0} strategies`);
    
    // Map the strategies array to Strategy objects with IDs
    const formattedStrategies = (strategiesContent.strategies || []).map(
      (strategy: string, index: number) => ({
        id: index + 1,
        text: strategy
      })
    );
    
    return {
      data: {
        strategies: formattedStrategies,
        documentId: document_id
      },
      error: null
    };
  } catch (parseError) {
    logger.error("Failed to parse content:", parseError);
    return {
      data: null,
      error: { message: "Invalid response format from API" }
    };
  }
};
